import { lazy } from 'react';
import type { BudgetingSectionDef } from './context';

// Pages and tabs load when first opened.
const HeadsTab = lazy(() => import('./components/StructureTabs').then((m) => ({ default: m.HeadsTab })));
const ProgrammesTab = lazy(() => import('./components/StructureTabs').then((m) => ({ default: m.ProgrammesTab })));
const PlacesTab = lazy(() => import('./components/StructureTabs').then((m) => ({ default: m.PlacesTab })));
const FundsTab = lazy(() => import('./components/StructureTabs').then((m) => ({ default: m.FundsTab })));
const CostCentresTab = lazy(() => import('./components/StructureTabs').then((m) => ({ default: m.CostCentresTab })));
const ChartTab = lazy(() => import('./components/ChartTab').then((m) => ({ default: m.ChartTab })));
const CycleTab = lazy(() => import('./components/CycleTabs').then((m) => ({ default: m.CycleTab })));
const CalendarTab = lazy(() => import('./components/CycleTabs').then((m) => ({ default: m.CalendarTab })));
const CircularTab = lazy(() => import('./components/CycleTabs').then((m) => ({ default: m.CircularTab })));
const CeilingsTab = lazy(() => import('./components/CycleTabs').then((m) => ({ default: m.CeilingsTab })));
const VersionsTab = lazy(() => import('./components/CycleTabs').then((m) => ({ default: m.VersionsTab })));
const SubmissionsTab = lazy(() => import('./components/PreparationTabs').then((m) => ({ default: m.SubmissionsTab })));
const StandardCostsTab = lazy(() => import('./components/PreparationTabs').then((m) => ({ default: m.StandardCostsTab })));
const SpreadProfilesTab = lazy(() => import('./components/PreparationTabs').then((m) => ({ default: m.SpreadProfilesTab })));
const TemplatesTab = lazy(() => import('./components/MethodsTabs').then((m) => ({ default: m.TemplatesTab })));
const DriversTab = lazy(() => import('./components/MethodsTabs').then((m) => ({ default: m.DriversTab })));
const RevenueStreamsTab = lazy(() => import('./components/MethodsTabs').then((m) => ({ default: m.RevenueStreamsTab })));
const CapitalProjectsTab = lazy(() => import('./components/MethodsTabs').then((m) => ({ default: m.CapitalProjectsTab })));
const ConsolidationTab = lazy(() => import('./components/ReviewTabs').then((m) => ({ default: m.ConsolidationTab })));
const HearingsTab = lazy(() => import('./components/ReviewTabs').then((m) => ({ default: m.HearingsTab })));
const AppropriationTab = lazy(() => import('./components/ReviewTabs').then((m) => ({ default: m.AppropriationTab })));
const LayoutsTab = lazy(() => import('./components/ReviewTabs').then((m) => ({ default: m.LayoutsTab })));
const ChainTab = lazy(() => import('./components/ReviewTabs').then((m) => ({ default: m.ChainTab })));
const AppropriationLoadTab = lazy(() => import('./components/ExecutionTabs').then((m) => ({ default: m.AppropriationLoadTab })));
const ReleasesTab = lazy(() => import('./components/ExecutionTabs').then((m) => ({ default: m.ReleasesTab })));
const CommitmentsTab = lazy(() => import('./components/ExecutionTabs').then((m) => ({ default: m.CommitmentsTab })));
const AvailabilityTab = lazy(() => import('./components/ExecutionTabs').then((m) => ({ default: m.AvailabilityTab })));
const FundTrackingTab = lazy(() => import('./components/ExecutionTabs').then((m) => ({ default: m.FundTrackingTab })));
const ChangesTab = lazy(() => import('./components/ChangesTabs').then((m) => ({ default: m.ChangesTab })));
const ChangesSummaryTab = lazy(() => import('./components/ChangesTabs').then((m) => ({ default: m.ChangesSummaryTab })));
const ExcessTab = lazy(() => import('./components/ChangesTabs').then((m) => ({ default: m.ExcessTab })));
const YearEndTab = lazy(() => import('./components/ChangesTabs').then((m) => ({ default: m.YearEndTab })));
const OutputsTab = lazy(() => import('./components/PerformanceTabs').then((m) => ({ default: m.OutputsTab })));
const PerformanceReportTab = lazy(() => import('./components/PerformanceTabs').then((m) => ({ default: m.PerformanceReportTab })));
const ExchangeRatesTab = lazy(() => import('./components/CompanyTabs').then((m) => ({ default: m.ExchangeRatesTab })));
const ForecastsTab = lazy(() => import('./components/CompanyTabs').then((m) => ({ default: m.ForecastsTab })));
const AccrualCashTab = lazy(() => import('./components/CompanyTabs').then((m) => ({ default: m.AccrualCashTab })));
const StatementsTab = lazy(() => import('./components/CompanyTabs').then((m) => ({ default: m.StatementsTab })));
const DonorGrantsTab = lazy(() => import('./components/CompanyTabs').then((m) => ({ default: m.DonorGrantsTab })));
const ProfileTab = lazy(() => import('./components/SettingsTabs').then((m) => ({ default: m.ProfileTab })));
const SegmentsTab = lazy(() => import('./components/SettingsTabs').then((m) => ({ default: m.SegmentsTab })));
const MigrationTab = lazy(() => import('./components/MigrationTab').then((m) => ({ default: m.MigrationTab })));

/**
 * The budgeting module's sections, one per menu item, each with its leaves. The URL's last segment
 * names the leaf, so every tab is a real address; the rights are read once by the page.
 */
export const budgetingSections = {
  cycle: {
    area: 'Accounting · Budgeting', title: 'Budget Cycle', basePath: '/budgeting/cycle',
    description: 'The year’s cycle from the circular to the appropriation: its calendar of dated stages, the circular and its guidelines, the ceilings and their distribution, and the versions of the budget.',
    views: [
      { key: 'cycle', label: 'Cycle' },
      { key: 'calendar', label: 'Calendar' },
      { key: 'circular', label: 'Circular' },
      { key: 'ceilings', label: 'Ceilings' },
      { key: 'versions', label: 'Versions' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'calendar': return <CalendarTab ctx={ctx} />;
        case 'circular': return <CircularTab ctx={ctx} />;
        case 'ceilings': return <CeilingsTab ctx={ctx} />;
        case 'versions': return <VersionsTab ctx={ctx} />;
        default: return <CycleTab ctx={ctx} />;
      }
    },
  } satisfies BudgetingSectionDef<'cycle' | 'calendar' | 'circular' | 'ceilings' | 'versions'>,

  preparation: {
    area: 'Accounting · Budgeting', title: 'Preparation', basePath: '/budgeting/preparation',
    description: 'Each unit\u2019s budget for the cycle, entered line by line, costed from the standard cost tables, spread over the months and submitted up the chain.',
    views: [
      { key: 'submissions', label: 'Budgets' },
      { key: 'standard-costs', label: 'Standard costs' },
      { key: 'spread-profiles', label: 'Spread profiles' },
    ],
    render: (view, ctx) => (view === 'standard-costs' ? <StandardCostsTab ctx={ctx} /> : view === 'spread-profiles' ? <SpreadProfilesTab ctx={ctx} /> : <SubmissionsTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'submissions' | 'standard-costs' | 'spread-profiles'>,

  methods: {
    area: 'Accounting · Budgeting', title: 'Methods', basePath: '/budgeting/methods',
    description: 'The methods behind a line: configurable templates with their fields, formula and rules; the drivers formula budgeting multiplies; revenue streams priced as rate \u00d7 volume; capital projects phased over the years.',
    views: [
      { key: 'templates', label: 'Method templates' },
      { key: 'drivers', label: 'Drivers' },
      { key: 'revenue-streams', label: 'Revenue streams' },
      { key: 'capital-projects', label: 'Capital projects' },
    ],
    render: (view, ctx) => (view === 'drivers' ? <DriversTab ctx={ctx} /> : view === 'revenue-streams' ? <RevenueStreamsTab ctx={ctx} /> : view === 'capital-projects' ? <CapitalProjectsTab ctx={ctx} /> : <TemplatesTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'templates' | 'drivers' | 'revenue-streams' | 'capital-projects'>,

  review: {
    area: 'Accounting · Budgeting', title: 'Review', basePath: '/budgeting/review',
    description: 'The budget rolled up to the head and the total: each level\u2019s recommendation, the hearings, the approvals outside the system that set the appropriated version, and the Government layouts.',
    views: [
      { key: 'consolidation', label: 'Consolidation' },
      { key: 'hearings', label: 'Hearings' },
      { key: 'appropriation', label: 'Appropriation' },
      { key: 'layouts', label: 'Layouts' },
    ],
    render: (view, ctx) => (view === 'hearings' ? <HearingsTab ctx={ctx} /> : view === 'appropriation' ? <AppropriationTab ctx={ctx} /> : view === 'layouts' ? <LayoutsTab /> : <ConsolidationTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'consolidation' | 'hearings' | 'appropriation' | 'layouts'>,

  execution: {
    area: 'Accounting · Budgeting', title: 'Execution', basePath: '/budgeting/execution',
    description: 'The budget in force: the appropriation loaded as the ledger\u2019s spending limit, the funding releases, the commitment ledger, what a line has available before a document is raised, and a fund tracked by place and project.',
    views: [
      { key: 'appropriation', label: 'Appropriation load' },
      { key: 'releases', label: 'Releases' },
      { key: 'commitments', label: 'Commitments' },
      { key: 'availability', label: 'Availability' },
      { key: 'funds', label: 'Fund tracking' },
    ],
    render: (view, ctx) => (view === 'releases' ? <ReleasesTab ctx={ctx} /> : view === 'commitments' ? <CommitmentsTab ctx={ctx} /> : view === 'availability' ? <AvailabilityTab /> : view === 'funds' ? <FundTrackingTab /> : <AppropriationLoadTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'appropriation' | 'releases' | 'commitments' | 'availability' | 'funds'>,

  changes: {
    area: 'Accounting · Budgeting', title: 'Changes', basePath: '/budgeting/changes',
    description: 'Every move of the budget in force after appropriation: virements under the rules, supplementary budgets with their authority, reductions and freezes, the excess register, and the year end.',
    views: [
      { key: 'virements', label: 'Virements' },
      { key: 'supplementary', label: 'Supplementaries' },
      { key: 'reductions', label: 'Reductions and freezes' },
      { key: 'summary', label: 'Original · changes · revised' },
      { key: 'excess', label: 'Excess expenditure' },
      { key: 'year-end', label: 'Year end' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'supplementary': return <ChangesTab ctx={ctx} kinds={['Supplementary', 'ExcessRegularisation']} title="Supplementary budgets" intro="Money added under an Act, a warrant, a resolution or a minute; one spent on a warrant is flagged until the Act regularises it. An excess regularisation brings the budget up to spending already made." />;
        case 'reductions': return <ChangesTab ctx={ctx} kinds={['Reduction', 'Freeze', 'CarryOver']} title="Reductions, freezes and carry-overs" intro="A reduction or freeze takes money away across lines, by a percentage or by hand; a freeze is reversed when the money is released again. A carry-over takes open commitments and project balances into the next year." />;
        case 'summary': return <ChangesSummaryTab />;
        case 'excess': return <ExcessTab ctx={ctx} />;
        case 'year-end': return <YearEndTab ctx={ctx} />;
        default: return <ChangesTab ctx={ctx} kinds={['Virement']} title="Virements" intro="Money moved between lines under the rules: a line gives up no more than the percentage allowed, personnel, capital and restricted funds as the rules say; the Controlling Officer approves within the delegation, the Budget Office and the Treasury above it." />;
      }
    },
  } satisfies BudgetingSectionDef<'virements' | 'supplementary' | 'reductions' | 'summary' | 'excess' | 'year-end'>,

  performance: {
    area: 'Accounting · Budgeting', title: 'Performance', basePath: '/budgeting/performance',
    description: 'What the money delivered: each output\u2019s indicators against their targets quarter by quarter with the evidence, its cost per unit from the activity-tagged ledger, and the mid-year and annual performance report per head.',
    views: [
      { key: 'outputs', label: 'Outputs and indicators' },
      { key: 'report', label: 'Performance report' },
    ],
    render: (view, ctx) => (view === 'report' ? <PerformanceReportTab /> : <OutputsTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'outputs' | 'report'>,

  company: {
    area: 'Accounting · Budgeting', title: 'Company and Accrual', basePath: '/budgeting/company',
    description: 'The company\u2019s budgeting: the budgeted income statement, balance sheet and cash flow against the ledger, rolling forecasts, the accrual-to-cash plan, the budget exchange rates and their variance, and the donor grants\u2019 report.',
    views: [
      { key: 'statements', label: 'Statements' },
      { key: 'forecasts', label: 'Rolling forecasts' },
      { key: 'accrual-cash', label: 'Accrual and cash' },
      { key: 'exchange-rates', label: 'Exchange rates' },
      { key: 'donors', label: 'Donor grants' },
    ],
    render: (view, ctx) => (view === 'forecasts' ? <ForecastsTab ctx={ctx} /> : view === 'accrual-cash' ? <AccrualCashTab /> : view === 'exchange-rates' ? <ExchangeRatesTab ctx={ctx} /> : view === 'donors' ? <DonorGrantsTab /> : <StatementsTab />),
  } satisfies BudgetingSectionDef<'statements' | 'forecasts' | 'accrual-cash' | 'exchange-rates' | 'donors'>,

  structure: {
    area: 'Accounting · Budgeting', title: 'Structure', basePath: '/budgeting/structure',
    description: 'What every budget line is classified by: heads and the cost centres that roll up to them, programmes down to outputs and activities, places, funds, and the chart’s economic classification.',
    views: [
      { key: 'heads', label: 'Heads and hierarchy' },
      { key: 'programmes', label: 'Programmes and outputs' },
      { key: 'cost-centres', label: 'Cost centres' },
      { key: 'places', label: 'Places' },
      { key: 'funds', label: 'Funds' },
      { key: 'chart', label: 'Chart of accounts' },
    ],
    render: (view, ctx) => {
      switch (view) {
        case 'programmes': return <ProgrammesTab ctx={ctx} />;
        case 'cost-centres': return <CostCentresTab ctx={ctx} />;
        case 'places': return <PlacesTab ctx={ctx} />;
        case 'funds': return <FundsTab ctx={ctx} />;
        case 'chart': return <ChartTab ctx={ctx} />;
        default: return <HeadsTab ctx={ctx} />;
      }
    },
  } satisfies BudgetingSectionDef<'heads' | 'programmes' | 'cost-centres' | 'places' | 'funds' | 'chart'>,

  settings: {
    area: 'Accounting · Budgeting', title: 'Budgeting Settings', basePath: '/budgeting/settings',
    description: 'The profile the organisation budgets with and the words it uses, which classification segments are on and which are mandatory, and the module’s controls.',
    views: [
      { key: 'profile', label: 'Profile and terms' },
      { key: 'segments', label: 'Segments and controls' },
      { key: 'chain', label: 'Approval chain' },
      { key: 'migration', label: 'Going live (imports)' },
    ],
    render: (view, ctx) => (view === 'segments' ? <SegmentsTab ctx={ctx} /> : view === 'chain' ? <ChainTab ctx={ctx} /> : view === 'migration' ? <MigrationTab ctx={ctx} /> : <ProfileTab ctx={ctx} />),
  } satisfies BudgetingSectionDef<'profile' | 'segments' | 'chain' | 'migration'>,
};
